import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import ChatThread from './components/ChatThread';
import DataPanel from './components/DataPanel';
import MessageInput from './components/MessageInput';
import StageProgress from './components/StageProgress';
import BusinessPlanPanel from './components/BusinessPlanPanel';
import FinancialPlanPanel from './components/FinancialPlanPanel';
import SbaPackagePanel from './components/SbaPackagePanel';
import ActualsUploadPanel from './components/ActualsUploadPanel';
import RightPanelTabs from './components/RightPanelTabs';
import AuthPage from './pages/AuthPage';
import SignupComplete from './pages/SignupComplete';
import PlanView from './pages/PlanView';
import {
  UnauthorizedError,
  abandonSession,
  createSession,
  getActiveSession,
  getPlan,
  getSession,
  getSessionMessages,
  getSessionState,
  getUserProfile,
  registerTokenGetter,
  sendMessageSSE,
} from './services/api';
import { loadConfig } from './services/config';
import './App.css';

const SESSION_KEY = 'sigmacore_session_id';

// Plain-English labels for interview_stage values, used in the
// "welcome back" bubble. Covers every per-vertical stage plus the F3
// business-plan stages. `complete` is intentionally absent — when stage ===
// 'complete' the app jumps straight to the plan view rather than showing a
// chat bubble.
const STAGE_LABELS = {
  concept: 'business concept',
  location: 'location and space',
  revenue: 'revenue planning',
  costs: 'operating costs',
  cogs: 'cost of goods',
  opex: 'operating expenses',
  capital: 'startup costs',
  financing: 'financing',
  ar_timing: 'AR and cash timing',
  growth: 'growth assumptions',
  // F5 operating-business stages — without these the welcome-back bubble
  // leaked raw stage ids ("you're at the capital_expenses stage").
  actuals_upload: 'financial statement upload',
  actuals_confirm: 'confirming your actuals',
  loan_purpose: 'loan purpose',
  capital_expenses: 'use of funds',
  pfs_collect: 'personal financial statement',
  bp_collect: 'business plan — info',
  bp_generate: 'business plan — writing',
  bp_complete: 'business plan complete',
};

// Full text for the brand-new-session welcome. Lives outside the
// component so it's importable by tests and doesn't get rebuilt on
// every render. Trailing question gives the user an explicit cue to
// answer the first interview field. Closing line tells the user they
// can continue in Spanish — Eliza follows the user's language from
// there (see _language_matching_block in orchestrator.py).
const NEW_SESSION_WELCOME =
  "Hi, I'm Eliza! I help small business owners build complete " +
  "financial plans and SBA loan packages — no spreadsheets or " +
  "finance background needed. To get started, what kind of " +
  "business are you planning?" +
  "\n\n" +
  "Puede continuar en español si lo prefiere — solo escriba su " +
  "próxima respuesta en español y seguiré la conversación en ese " +
  "idioma.";

function buildWelcomeBackMessage(stage, nextQuestion) {
  const label = STAGE_LABELS[stage] || stage;
  // The trailing next-question sentence comes from the server so it
  // stays in sync with the schema. Falls back to a soft prompt when
  // /active didn't supply one (unknown industry, complete stage, etc).
  const tail = nextQuestion || 'What would you like to work on next?';
  return `Welcome back! We're working on your ${label}. ${tail}`;
}

// Small helper — crypto.randomUUID is available in all evergreen browsers,
// but we keep a fallback so local dev / older browsers don't blow up.
function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export default function App() {
  // Gate the whole app behind loadConfig() — no API call may fire
  // until we know which backend to talk to. Without this gate a
  // protected route's fetch could be kicked off mid-render against
  // an unresolved base URL.
  const [configReady, setConfigReady] = useState(false);

  // Auth is owned by Clerk (F1). `isSignedIn` replaces the old
  // localStorage-JWT `isAuthenticated` flag; `getToken` is the async
  // session-token getter we bridge into the api layer. Declared at the
  // very top of the component so the effects below can reference it in
  // their dependency arrays without hitting a temporal-dead-zone error.
  const { isLoaded: authLoaded, isSignedIn, getToken, signOut } = useAuth();

  // Profile-completion gate. 'unknown' until we've fetched /users/me;
  // 'incomplete' shows SignupComplete before the interview; 'complete'
  // releases the app. company_name is a required Eliza field the Clerk
  // JWT doesn't carry, so first login always starts 'incomplete'.
  const [profileStatus, setProfileStatus] = useState('unknown');

  useEffect(() => {
    let cancelled = false;
    loadConfig().finally(() => {
      if (!cancelled) setConfigReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Bridge Clerk's async getToken into the non-React api layer so every
  // request can attach a fresh Bearer token. Registered before any
  // protected fetch fires (this effect is declared ahead of the profile
  // and init effects). Cleared on sign-out / unmount.
  useEffect(() => {
    registerTokenGetter(isSignedIn ? getToken : null);
    return () => registerTokenGetter(null);
  }, [isSignedIn, getToken]);

  // Once signed in (and config is ready so getApiUrl() works), fetch the
  // profile to decide whether to show the completion screen.
  useEffect(() => {
    if (!isSignedIn || !configReady) {
      setProfileStatus('unknown');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await getUserProfile();
        if (cancelled) return;
        setProfileStatus(profile.profile_complete ? 'complete' : 'incomplete');
      } catch (err) {
        if (cancelled) return;
        // A 401 is handled by the unauthorized listener (signs out). For
        // any other failure, fall through to the completion screen so the
        // user can supply company_name rather than being stuck on a
        // blank loader.
        if (!(err instanceof UnauthorizedError)) {
          setProfileStatus('incomplete');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, configReady]);

  const [sessionId, setSessionId] = useState(null);
  const [stage, setStage] = useState('concept');
  // F5: per-session active stage list (from the session response) for the
  // stepper. null → StageProgress falls back to the static vertical stages.
  const [activeStages, setActiveStages] = useState(null);
  // The session's vertical drives the vertical-aware StageProgress + DataPanel.
  // Set from the session-state fetch as soon as it resolves (defaults
  // restaurant for the first render before the fetch lands).
  const [vertical, setVertical] = useState('restaurant');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [showPlan, setShowPlan] = useState(false);

  // ── DataPanel state ───────────────────────────────────────────────
  // collectedInputs is the map shape returned by GET /sessions/{id}:
  //   { [field_name]: { field_name, value, source_tag, confirmed_at } }
  // Refreshed after every assistant turn so the right-hand panel
  // tracks the interview live without a page reload.
  const [collectedInputs, setCollectedInputs] = useState({});
  // FinancialPlanOutput shape, populated once calculate_plan has run.
  const [latestPlan, setLatestPlan] = useState(null);
  // F3/F4 phase state. bpStatus (null/collecting/generating/complete) gates the
  // SBA panel; refreshSignal is bumped after each chat turn so the BP/SBA
  // panels re-fetch their status (covers generation and regeneration).
  const [bpStatus, setBpStatus] = useState(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  // Right-panel view toggle: once the business plan is complete the user can
  // switch the right panel from the BusinessPlanPanel to the SbaPackagePanel.
  const [sbaView, setSbaView] = useState(false);
  // Mobile drawer toggle. Desktop CSS hides the toggle button and
  // forces the panel visible regardless of this flag.
  const [dataPanelOpen, setDataPanelOpen] = useState(false);

  // Tracks every message id we have ever rendered, so duplicate SSE events
  // (StrictMode re-mounts, double form-submits, retries) are never rendered
  // twice. A Set in a ref survives re-renders without triggering them.
  const seenMessageIds = useRef(new Set());

  // Global listener: the api layer dispatches this when any request returns
  // 401 (expired / revoked token). Flip back to the login view.
  useEffect(() => {
    const onUnauth = () => {
      setSessionId(null);
      setProfileStatus('unknown');
      localStorage.removeItem(SESSION_KEY);
      // Reset the init guards so a fresh sign-in restarts the
      // Step 1 → Step 2 sequence from scratch.
      initStarted.current = false;
      initInFlight.current = null;
      // Clerk owns the session — signing out routes back to the
      // sign-in screen (isSignedIn flips false).
      signOut().catch(() => { /* best effort */ });
    };
    window.addEventListener('sigmacore:unauthorized', onUnauth);
    return () => window.removeEventListener('sigmacore:unauthorized', onUnauth);
  }, [signOut]);

  // Browser-level "Are you sure?" prompt when the user tries to close
  // the tab, hit back/forward, or hard-reload while an interview is
  // in progress. The collected_inputs are persisted server-side, but
  // the in-memory message thread is not — so the warning is also a
  // hint that they're about to lose visible context.
  //
  // Conditions:
  //  - sessionId present (an interview actually exists)
  //  - stage !== 'complete' (the plan is finalised — nothing to lose)
  // beforeunload only fires on real browser navigations; React state
  // transitions like Logout / New Plan / "View Your Plan" don't
  // trigger it. The listener depends on [sessionId, stage] so it's
  // re-bound when those change, picking up the latest closure.
  useEffect(() => {
    const sessionActive = !!sessionId;
    const handler = (e) => {
      if (sessionActive && stage !== 'complete') {
        // Modern browsers ignore custom strings here and show their
        // own canonical "Leave site?" dialog — both `preventDefault`
        // and `returnValue` are required across the browser matrix.
        e.preventDefault();
        e.returnValue = '';
      }
    };
    // Only register when we'd actually warn — saves the no-op handler
    // overhead on the login screen and the post-plan view.
    if (!sessionActive || stage === 'complete') return undefined;
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [sessionId, stage]);

  // Promise resolving to the initialised session id. Every caller that
  // needs a session (e.g. handleSend) awaits this — so nothing can
  // bypass the strict Step 1 → Step 2 ordering below. A ref (not
  // state) so reads are synchronous.
  const initInFlight = useRef(null);

  // Guard so the Step 1 / Step 2 sequence runs at most once per
  // authenticated session. Without this, StrictMode's double-effect
  // would fire the whole init twice and potentially issue two
  // POST /sessions calls in the null-active branch.
  const initStarted = useRef(false);

  // ── DataPanel refresh helper ──────────────────────────────────────
  // Defined BEFORE the init useEffect that references it via the
  // `task.then(...)` callback and via its deps array. Moving it here
  // avoids the "Cannot access 'refreshPanelData' before initialization"
  // TDZ error that fires when React evaluates the deps array on first
  // render — the deps reference must be live by that point.
  //
  // Pulls the canonical session state from the server (collected_inputs
  // + interview_stage). Best-effort — failures don't surface to the
  // user, the panel just shows the last successful snapshot.
  // When the interview has completed, also pulls the financial plan
  // so the Key Financials section materialises automatically.
  const refreshPanelData = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const state = await getSessionState(sid);
      setCollectedInputs(state.collected_inputs || {});
      // The /state response carries the freshest stage + vertical; mirror them
      // so the welcome-back / send flow and the vertical-aware panels stay in
      // sync without an extra refetch.
      if (state.interview_stage) setStage(state.interview_stage);
      if (state.vertical) setVertical(state.vertical);
      // F5: the session's active stage list drives the stepper (operating
      // businesses get the actuals stages; pre-launch gets the static list).
      if (state.active_stages) setActiveStages(state.active_stages);
      // F3/F4 phase state now travels on the session response.
      setBpStatus(state.bp_status ?? null);
      if (state.interview_stage === 'complete') {
        try {
          const p = await getPlan(sid);
          setLatestPlan(p);
        } catch {
          /* getPlan can 404 if calculate_plan hasn't actually run yet */
        }
      }
    } catch (err) {
      if (err instanceof UnauthorizedError) return;
      // Silent — the panel is informational, not critical.
    }
  }, []);

  // ──────────────────────────────────────────────────────────────────
  // STRICT initialization flow. Contract, in order:
  //   Step 1: GET /sessions/active  (always first)
  //   Step 2a: if 200 with session_id → resume
  //   Step 2b: if null/404          → POST /sessions to create
  // No other call site in this app may fire POST /sessions. All
  // user-facing code paths await `initInFlight.current` to get the
  // session id.
  // Waits for `isSignedIn`, `configReady`, AND a complete profile —
  // getApiUrl() throws if loadConfig() hasn't populated the runtime
  // config yet, which was the original reason /active calls were
  // silently failing.
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isSignedIn || !configReady || profileStatus !== 'complete') return;
    if (initStarted.current) return;
    initStarted.current = true;

    let cancelled = false;

    const task = (async () => {
      // ---- Step 1 --------------------------------------------------
      // eslint-disable-next-line no-console
      console.log('[DEBUG] Step 1: Checking for active session...');

      let active = null;
      try {
        active = await getActiveSession();
      } catch (err) {
        // Network / 4xx / 5xx from /active — treat as "no active"
        // and drop into Step 2b. We never skip to POST without at
        // least attempting /active first.
        // eslint-disable-next-line no-console
        console.log('[DEBUG] Step 1 error (treating as no active session):',
                    err && err.message);
        active = null;
      }
      if (cancelled) return null;

      // ---- Step 2a: found → resume --------------------------------
      if (active && active.session_id) {
        // eslint-disable-next-line no-console
        console.log(
          '[DEBUG] Step 2a: Found session ' + active.session_id +
          ' - resuming (is_new=' + active.is_new + ')'
        );

        // If the resumed session is already complete, restore the
        // plan data and route directly to the plan view. PlanView
        // also fetches the plan on its own mount, but pre-populating
        // App-level state (collected_inputs + latest_plan) means the
        // DataPanel's Key Financials section is ready immediately,
        // and the welcome bubble in the chat (visible if the user
        // clicks "Back to Chat") shows the right text.
        if (active.interview_stage === 'complete') {
          localStorage.setItem(SESSION_KEY, active.session_id);
          setSessionId(active.session_id);
          setStage('complete');

          // Restore latest_plan + collected_inputs in parallel.
          // Both are best-effort — if either fails, PlanView's own
          // fetch retries and surfaces a proper error message.
          try {
            const [restoredPlan, restoredState] = await Promise.all([
              getPlan(active.session_id).catch(() => null),
              getSessionState(active.session_id).catch(() => null),
            ]);
            if (cancelled) return null;
            if (restoredPlan) setLatestPlan(restoredPlan);
            if (restoredState && restoredState.collected_inputs) {
              setCollectedInputs(restoredState.collected_inputs);
            }
          } catch {
            /* swallow — PlanView re-fetches on its own */
          }

          // Welcome bubble for the completed-session path. Visible
          // when the user navigates back to chat from the plan view.
          const businessName = active.business_name || 'your business';
          appendMessageIfNew({
            id: `welcome:complete:${active.session_id}`,
            role: 'assistant',
            eliza: true,
            content:
              `Welcome back! Your financial plan for ${businessName} is ready. ` +
              `You can view your plan below or start a new one.`,
          });

          setShowPlan(true);
          return active.session_id;
        }

        // Read the cached id BEFORE overwriting localStorage — otherwise
        // the "did the server agree with our cache?" check below is
        // always vacuously true and the getSessionState refetch never
        // runs.
        const cachedSid = localStorage.getItem(SESSION_KEY);

        localStorage.setItem(SESSION_KEY, active.session_id);
        setSessionId(active.session_id);
        setStage(active.interview_stage || 'concept');

        // Welcome bubble — UI only, never sent to the backend.
        // Branch on the server's is_new flag: a session created via
        // "New Plan" then revisited (logout/login) should still see
        // the long new-session welcome until the user has actually
        // engaged with it.
        if (active.is_new) {
          appendMessageIfNew({
            id: `welcome:new:${active.session_id}`,
            role: 'assistant',
            eliza: true,
            content: NEW_SESSION_WELCOME,
          });
        } else {
          // Rebuild the thread from the server-side transcript so the
          // conversation survives logout/login and device switches (browser
          // React state alone can't). Fall back to the short welcome-back
          // bubble when there's no stored history (older sessions).
          let history = [];
          try {
            history = await getSessionMessages(active.session_id);
          } catch {
            history = [];
          }
          if (cancelled) return null;
          if (history.length) {
            history.forEach((m, i) => {
              const isAssistant = m.role === 'assistant';
              appendMessageIfNew({
                id: `hist:${active.session_id}:${i}`,
                role: isAssistant ? 'assistant' : 'user',
                eliza: isAssistant,
                content: m.content,
              });
            });
          } else {
            appendMessageIfNew({
              id: `welcome:back:${active.session_id}`,
              role: 'assistant',
              eliza: true,
              content: buildWelcomeBackMessage(
                active.interview_stage || 'concept',
                active.next_question,
              ),
            });
          }
        }

        // Skip the extra getSessionState() round-trip when our cache
        // already matches the server — saves one request on the
        // common same-browser reload case.
        if (cachedSid !== active.session_id) {
          try {
            const full = await getSessionState(active.session_id);
            if (cancelled) return null;
            setStage(full.interview_stage);
          } catch {
            /* state refetch is advisory — don't block on failure */
          }
        }
        return active.session_id;
      }

      // ---- Step 2b: null/404 → create new --------------------------
      // eslint-disable-next-line no-console
      console.log('[DEBUG] Step 2b: No active session - creating new');
      try {
        const { session_id: newSid } = await createSession();
        if (cancelled) return null;
        localStorage.setItem(SESSION_KEY, newSid);
        setSessionId(newSid);
        setStage('concept');

        // Brand-new-session welcome — UI only.
        appendMessageIfNew({
          id: `welcome:new:${newSid}`,
          role: 'assistant',
          eliza: true,
          content: NEW_SESSION_WELCOME,
        });

        return newSid;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[DEBUG] Step 2b: createSession failed:',
                    err && err.message);
        throw err;
      }
    })();

    initInFlight.current = task;

    // Once init resolves, hydrate the DataPanel from the live session
    // state. Without this the panel would render empty until the user
    // sent their first message.
    task.then((sid) => {
      if (cancelled || !sid) return;
      refreshPanelData(sid);
    }).catch(() => { /* init failures already logged inside the task */ });

    return () => { cancelled = true; };
  }, [isSignedIn, configReady, profileStatus, refreshPanelData]);

  // Consumer API — awaits the strict init. Never creates a session
  // itself (that can only happen inside the Step 2b branch above).
  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    if (initInFlight.current) {
      const sid = await initInFlight.current;
      if (sid) return sid;
    }
    // If we somehow land here (init not yet started, or the promise
    // resolved to null), fall back to localStorage. We deliberately do
    // NOT fire POST /sessions here — the Step 1 / Step 2 contract says
    // creation is only allowed inside the init effect, AFTER /active
    // has been called.
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      setSessionId(stored);
      return stored;
    }
    throw new Error(
      'Session not initialised yet. The init effect should have run ' +
      'after login — this is a bug if you see it in production.'
    );
  }, [sessionId]);

  // Append a message only if its id hasn't been rendered yet. Both the
  // ref-Set and a defensive check inside the setState updater guard
  // against duplicates — the updater check handles the case where React
  // invokes the updater twice (StrictMode dev behavior).
  const appendMessageIfNew = useCallback((msg) => {
    if (!msg.id) msg.id = newId();
    if (seenMessageIds.current.has(msg.id)) return;
    seenMessageIds.current.add(msg.id);
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const handleSend = useCallback(
    async (text) => {
      setError(null);

      // User message gets a client-generated id so a double form-submit
      // (Enter-then-click, StrictMode double-effect, etc.) cannot produce
      // two identical user bubbles.
      appendMessageIfNew({ id: newId(), role: 'user', content: text });
      setIsTyping(true);

      // Track which done events we've already handled in this turn so
      // the stage state doesn't flip twice for the same event.
      const doneSeen = new Set();

      try {
        const sid = await ensureSession();

        sendMessageSSE(
          sid,
          text,
          // onText — server-issued id is the dedupe key.
          (content, eventId) => {
            setIsTyping(false);
            // Skip empty payloads — they would render as a blank bubble
            // and make the user think the assistant didn't answer.
            if (!content || !content.trim()) return;
            appendMessageIfNew({
              id: eventId || newId(),
              role: 'assistant',
              content,
            });
          },
          // onDone
          (newStage, eventId) => {
            setIsTyping(false);
            if (eventId) {
              if (doneSeen.has(eventId)) return;
              doneSeen.add(eventId);
            }
            if (newStage) setStage(newStage);
            // Refresh the right-hand DataPanel after every assistant
            // turn — collected_inputs may have grown, and stage may
            // have advanced. Fire-and-forget; failures are swallowed
            // inside the helper (panel is informational).
            refreshPanelData(sid);
            // Nudge the BP/SBA panels to re-fetch their status after every
            // turn — a section may have just generated or regenerated.
            setRefreshSignal((v) => v + 1);
          },
          // onError
          (errMsg) => {
            setIsTyping(false);
            setError(errMsg);
          }
        );
      } catch (err) {
        setIsTyping(false);
        // 401 path: handle401() in the api layer already cleared
        // local state and dispatched the unauthorized event. Don't
        // surface a "Create session failed: 401" banner — the user
        // is being redirected to the login page.
        if (err instanceof UnauthorizedError) return;
        setError(err.message);
      }
    },
    [ensureSession, appendMessageIfNew, refreshPanelData]
  );

  // Regeneration is conversational (no REST endpoint): we compose a chat
  // message and send it through the normal flow, so the orchestrator LLM
  // calls regenerate_bp_section. The message renders in the thread like any
  // other, and onDone bumps refreshSignal so the panel re-polls.
  const handleRegenerate = useCallback(
    (sectionTitle, instruction) => {
      const base = `Please regenerate the ${sectionTitle} section.`;
      handleSend(instruction ? `${base} ${instruction}` : base);
    },
    [handleSend]
  );

  const handleNewSession = async () => {
    // "New Plan" is an explicit destructive action — the user is
    // saying "leave this plan behind, start over". Unlike the login
    // init flow (which resumes /active), this one:
    //   1. asks for confirmation,
    //   2. abandons the CURRENT session on the server so it stops
    //      showing up on /sessions/active,
    //   3. creates a fresh session directly via POST /sessions.
    // It deliberately does NOT re-enter the /active → resume path
    // because that would just resurrect the session we're trying to
    // leave behind.
    const ok = window.confirm(
      'Starting a new plan will leave your current plan incomplete. ' +
      'Are you sure?'
    );
    if (!ok) return;

    // 1. Abandon the current session (best-effort — a failed abandon
    //    shouldn't block the user from starting over).
    const currentSid = sessionId || localStorage.getItem(SESSION_KEY);
    if (currentSid) {
      try {
        await abandonSession(currentSid);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[DEBUG] abandonSession failed (continuing anyway):',
                    err && err.message);
      }
    }

    // 2. Tear down local state BEFORE creating the new session so a
    //    stray render doesn't flash the old data.
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setStage('concept');
    setVertical('restaurant');
    setMessages([]);
    setError(null);
    setShowPlan(false);
    setCollectedInputs({});
    setLatestPlan(null);
    setBpStatus(null);
    setSbaView(false);
    seenMessageIds.current.clear();

    // 3. Create the replacement session DIRECTLY. Do NOT re-enter
    //    the init effect — it would call /active, which could return
    //    some other session that the user didn't explicitly choose.
    //    After creation, append the new-session welcome bubble so the
    //    user doesn't land on a blank screen.
    try {
      const { session_id: newSid } = await createSession();
      localStorage.setItem(SESSION_KEY, newSid);
      setSessionId(newSid);
      // The init promise is already resolved against the old session;
      // replace it with one that resolves to the new id so any
      // awaiting ensureSession() call sees the right session.
      initInFlight.current = Promise.resolve(newSid);
      initStarted.current = true;
      // Show Eliza's welcome immediately — same bubble the init
      // effect would emit on a fresh login.
      appendMessageIfNew({
        id: `welcome:new:${newSid}`,
        role: 'assistant',
        eliza: true,
        content: NEW_SESSION_WELCOME,
      });
    } catch (err) {
      if (err instanceof UnauthorizedError) return;
      setError(err.message);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem(SESSION_KEY);
    seenMessageIds.current.clear();
    // Drop any in-flight init state so the next login restarts the
    // Step 1 → Step 2 sequence from scratch.
    initInFlight.current = null;
    initStarted.current = false;
    setSessionId(null);
    setStage('concept');
    setVertical('restaurant');
    setMessages([]);
    setError(null);
    setShowPlan(false);
    setCollectedInputs({});
    setLatestPlan(null);
    setBpStatus(null);
    setSbaView(false);
    setProfileStatus('unknown');
    // Clerk owns the session — signOut() clears it and flips isSignedIn
    // to false, routing back to the AuthPage.
    try {
      await signOut();
    } catch { /* best effort — local state is already torn down */ }
  };

  // Persistent "Limited Beta" footer — fixed to the viewport so it shows on
  // every screen (loading, auth, profile, plan view, and the main app shell).
  const betaFooter = (
    <footer className="beta-footer">
      Eliza by SigmaCore — Limited Beta
    </footer>
  );

  // Loading gate — we need both the backend URL (configReady) and
  // Clerk's auth state (authLoaded) resolved before rendering anything
  // that might trigger a fetch or read isSignedIn.
  if (!configReady || !authLoaded) {
    return (
      <>
        <div className="app-loading">
          <div className="app-loading-spinner" aria-hidden="true" />
          <p>Loading SigmaCore…</p>
        </div>
        {betaFooter}
      </>
    );
  }

  // Signed-out users land on the Clerk-backed auth page — no app shell,
  // no protected calls.
  if (!isSignedIn) {
    return (
      <>
        <AuthPage />
        {betaFooter}
      </>
    );
  }

  // Signed in but we haven't resolved the profile yet — brief loader
  // while GET /users/me is in flight.
  if (profileStatus === 'unknown') {
    return (
      <>
        <div className="app-loading">
          <div className="app-loading-spinner" aria-hidden="true" />
          <p>Loading your profile…</p>
        </div>
        {betaFooter}
      </>
    );
  }

  // First login (or missing company_name) → collect the required profile
  // fields before the interview begins.
  if (profileStatus === 'incomplete') {
    return (
      <>
        <SignupComplete onComplete={() => setProfileStatus('complete')} />
        {betaFooter}
      </>
    );
  }

  const isComplete = stage === 'complete';
  // F3 business-plan phase — drives the tabbed Financial Data + Business Plan
  // panel. Includes pfs_collect (Personal Financial Statement collection) so the
  // Financial Data tab stays present alongside the Business Plan tab across the
  // ENTIRE business-plan phase, not just the collect/generate/complete stages.
  const isBpPhase = [
    'bp_collect', 'pfs_collect', 'bp_generate', 'bp_complete',
  ].includes(stage);
  // F5 upload-actuals phase (operating businesses) — drives the
  // ActualsUploadPanel during the upload + confirm stages. Guarded on the
  // CURRENT active_stages: if business_stage flips to pre_launch mid-interview,
  // the recomputed active_stages no longer contain the actuals stages, so a
  // stale interview_stage can't keep the ActualsUploadPanel pinned — we fall
  // back to the DataPanel. (Bugfix: previously keyed off the stage value alone.)
  const stageInActiveStages = !activeStages || activeStages.includes(stage);
  const isActualsPhase =
    ['actuals_upload', 'actuals_confirm'].includes(stage) && stageInActiveStages;

  if (showPlan && sessionId) {
    return (
      <>
        <PlanView sessionId={sessionId} onBack={() => setShowPlan(false)} />
        {betaFooter}
      </>
    );
  }

  return (
    <>
    <div className="app app-with-panel">
      <header className="app-header">
        <img className="app-logo" src="/eliza-logo.png" alt="Eliza" height="40" />
        <h1>SigmaCore.ai</h1>
        <span className="app-subtitle">Business Financial Planner</span>
        {/* Mobile-only: show/hide the DataPanel drawer. Desktop hides
            this button via CSS and keeps the panel pinned to the right. */}
        <button
          className="data-panel-toggle"
          aria-label="Toggle plan panel"
          onClick={() => setDataPanelOpen((v) => !v)}
        >
          📋 Plan
        </button>
        {/* Persistent path back to the financial plan (summary + xlsx/csv
            downloads). The old route was a ONE-TIME banner button in chat —
            once the user moved into the business-plan phase it vanished and
            the plan became unreachable (live-test issue 2). Business plan,
            SBA package, and financial plan are now all persistently
            reachable once they exist. */}
        {sessionId && (isComplete || isBpPhase || latestPlan) && (
          <button
            className="view-plan-btn header-plan-btn"
            onClick={() => setShowPlan(true)}
          >
            📊 Financial Plan
          </button>
        )}
        <button className="new-session-btn" onClick={handleNewSession}>
          New Plan
        </button>
        <button className="logout-btn" onClick={handleLogout}>
          Log out
        </button>
        <span className="eliza-brand">ELIZA</span>
      </header>

      <StageProgress currentStage={stage} vertical={vertical} activeStages={activeStages} />

      <div className="app-split">
        {/* Left column — chat */}
        <div className="chat-column">
          <main className="chat-container">
            {/* No hero splash — the first thing the user sees is Eliza's
                welcome chat bubble (emitted by the init effect). The bubble
                is intentionally indistinguishable in style from any other
                assistant message in the thread. */}
            <ChatThread
              messages={messages}
              isTyping={isTyping}
              typingLabel={
                isBpPhase
                  ? 'Writing your business plan — section-by-section progress '
                    + 'is in the panel on the right. Longer sections can take '
                    + 'a minute.'
                  : stage === 'growth' || stage === 'complete'
                  ? 'Working on it — calculating the full 3-year financial '
                    + 'model can take a moment.'
                  : null
              }
            />

            {error && <div className="error-banner">{error}</div>}

            {isComplete && (
              <div className="complete-banner">
                <p>Your financial plan is ready!</p>
                <button className="view-plan-btn" onClick={() => setShowPlan(true)}>
                  View Your Plan
                </button>
              </div>
            )}
          </main>

          <MessageInput onSend={handleSend} disabled={isTyping} />
        </div>

        {/* Right column — a single context-appropriate panel fills the slot.
            Exactly ONE of these renders (never DataPanel + a BP/SBA panel at
            once). On desktop the panel is always visible (CSS forces it open);
            on mobile the 📋 toggle controls dataPanelOpen and it slides in.
              - SBA package view (user opted in, plan complete) → SbaPackagePanel
              - business-plan phase → BusinessPlanPanel
              - otherwise (financial interview) → DataPanel */}
        {bpStatus === 'complete' && sessionId ? (
          // Business plan complete — the three-tab panel (incl. SBA Package)
          // appears AUTOMATICALLY; sbaView only picks which tab is fronted
          // (the user can always switch). Previously the SBA tab's existence
          // required a manual click to discover.
          <RightPanelTabs
            open={dataPanelOpen}
            onClose={() => setDataPanelOpen(false)}
            defaultTabKey={sbaView ? 'sba' : 'bp'}
            tabs={[
              {
                key: 'data',
                label: 'Financial Data',
                render: () => (
                  <DataPanel
                    embedded
                    collectedInputs={collectedInputs}
                    latestPlan={latestPlan}
                    vertical={vertical}
                  />
                ),
              },
              {
                key: 'plan',
                label: 'Financial Plan',
                render: () => (
                  <FinancialPlanPanel
                    embedded
                    sessionId={sessionId}
                    refreshSignal={refreshSignal}
                    onOpenFull={() => setShowPlan(true)}
                  />
                ),
              },
              {
                key: 'bp',
                label: 'Business Plan',
                render: () => (
                  <BusinessPlanPanel
                    embedded
                    sessionId={sessionId}
                    refreshSignal={refreshSignal}
                    onRegenerate={handleRegenerate}
                    onStatusChange={setBpStatus}
                  />
                ),
              },
              {
                key: 'sba',
                label: 'SBA Package',
                render: () => (
                  <SbaPackagePanel
                    embedded
                    sessionId={sessionId}
                    refreshSignal={refreshSignal}
                    onBack={() => setSbaView(false)}
                  />
                ),
              },
            ]}
          />
        ) : isActualsPhase && sessionId ? (
          <ActualsUploadPanel
            sessionId={sessionId}
            stage={stage}
            refreshSignal={refreshSignal}
            onSendMessage={handleSend}
            title={stage === 'actuals_upload'
              ? 'Upload Financials'
              : 'Review Figures'}
            open={dataPanelOpen}
            onClose={() => setDataPanelOpen(false)}
          />
        ) : isBpPhase && sessionId ? (
          // Business-plan phase — two tabs: Financial Data, Business Plan
          // (default), so the user can flip back to review their answers.
          <RightPanelTabs
            open={dataPanelOpen}
            onClose={() => setDataPanelOpen(false)}
            defaultTabKey="bp"
            tabs={[
              {
                key: 'data',
                label: 'Financial Data',
                render: () => (
                  <DataPanel
                    embedded
                    collectedInputs={collectedInputs}
                    latestPlan={latestPlan}
                    vertical={vertical}
                  />
                ),
              },
              {
                key: 'plan',
                label: 'Financial Plan',
                render: () => (
                  <FinancialPlanPanel
                    embedded
                    sessionId={sessionId}
                    refreshSignal={refreshSignal}
                    onOpenFull={() => setShowPlan(true)}
                  />
                ),
              },
              {
                key: 'bp',
                label: 'Business Plan',
                render: () => (
                  <BusinessPlanPanel
                    embedded
                    sessionId={sessionId}
                    refreshSignal={refreshSignal}
                    onRegenerate={handleRegenerate}
                    onStatusChange={setBpStatus}
                    onOpenSba={bpStatus === 'complete' ? () => setSbaView(true) : null}
                  />
                ),
              },
            ]}
          />
        ) : (
          <DataPanel
            collectedInputs={collectedInputs}
            latestPlan={latestPlan}
            vertical={vertical}
            open={dataPanelOpen}
            onClose={() => setDataPanelOpen(false)}
          />
        )}
      </div>
    </div>
    {betaFooter}
    </>
  );
}
